import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import {
  createDefaultSynthResource,
  normalizeSynthResource,
  SYNTH_DEFAULT_NOTE,
  SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS,
  normalizeSequenceEvent,
  normalizeSequenceTimeline,
  sortSequenceEvents,
} from './synthModel.js'
import { parseSynthFile } from './synthFormat.js'
import { importMidiFile } from './synthMidiImport.js'
import { useAudioStore } from './audioStore.js'
import { isDesktop, loadResourceLibrary, saveResourceLibrary } from '../services/resourceStorage.js'

const STORAGE_KEY = 'hasp-synth-library'

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const resources = JSON.parse(raw)
    return Array.isArray(resources) ? resources.map(normalizeSynthResource) : []
  } catch {
    return []
  }
}

export const useSynthStore = defineStore('synth', {
  // The browser library loads synchronously; the desktop app's SQLite
  // library arrives through load() at startup.
  state: () => ({
    synths: isDesktop() ? [] : loadFromStorage(),
    lastImportWarnings: [],
    loaded: !isDesktop(),
  }),
  getters: {
    all: (state) => state.synths,
  },
  actions: {
    _uniqueFilename(filename, exceptUuid = null) {
      const taken = new Set(this.synths.filter(s => s.uuid !== exceptUuid).map(s => s.filename).filter(Boolean))
      if (!taken.has(filename)) return filename
      const dot = filename.lastIndexOf('.')
      const stem = dot > 0 ? filename.slice(0, dot) : filename
      const ext = dot > 0 ? filename.slice(dot) : ''
      let n = 2
      while (taken.has(`${stem}-${n}${ext}`)) n++
      return `${stem}-${n}${ext}`
    },

    /** Load the desktop app's SQLite library (no-op in the browser, which loads synchronously). */
    async load() {
      if (!isDesktop()) return
      this.synths = (await loadResourceLibrary('synths', STORAGE_KEY)).map(normalizeSynthResource)
      this.loaded = true
    },

    _persist() {
      // Never let a save before load() replace the stored library with a partial one.
      if (!this.loaded) return
      try {
        saveResourceLibrary('synths', STORAGE_KEY, this.synths)
      } catch (e) {
        console.warn('Failed to persist synth library:', e)
      }
    },

    addSynth() {
      const synth = createDefaultSynthResource()
      synth.filename = this._uniqueFilename('new-synth.synth')
      this.synths.push(synth)
      this._persist()
      return synth.uuid
    },
    async importSynthFile(file) {
      this.lastImportWarnings = []
      const isMidi = /\.midi?$/i.test(file.name)
      if (!isMidi && !/\.synth$/i.test(file.name)) throw new Error('Unsupported synth import file. Choose a .synth, .mid, or .midi file.')
      const result = isMidi
        ? importMidiFile(await file.arrayBuffer())
        : parseSynthFile(await readFileAsText(file))
      const { synth: parsed, warnings } = result
      this.lastImportWarnings = warnings
      const stem = file.name.replace(/\.(?:synth|midi?)$/i, '')
      const synth = normalizeSynthResource({
        ...parsed,
        name: parsed.name && parsed.name !== 'New Synth' ? parsed.name : stem,
        filename: this._uniqueFilename(parsed.filename || `${stem}.synth`),
      })
      this.synths.push(synth)
      this._persist()
      return synth.uuid
    },
    removeSynth(uuid) {
      if (useAudioStore().all.some(resource => resource.type === 'synth' && resource.synthId === uuid)) {
        return false
      }
      const idx = this.synths.findIndex(s => s.uuid === uuid)
      if (idx >= 0) {
        this.synths.splice(idx, 1)
        this._persist()
        return true
      }
      return false
    },
    duplicateSynth(uuid) {
      const src = this.synths.find(s => s.uuid === uuid)
      if (!src) return null
      const copy = normalizeSynthResource({
        ...JSON.parse(JSON.stringify(src)),
        uuid: uuidv4(),
        name: src.name + ' (copy)',
      })
      this.synths.push(copy)
      this._persist()
      return copy.uuid
    },
    getSynth(uuid) {
      return this.synths.find(s => s.uuid === uuid) ?? null
    },
    updateSynth(uuid, patch) {
      const synth = this.synths.find(s => s.uuid === uuid)
      if (!synth) return
      if (patch.filename && patch.filename !== synth.filename) {
        patch = { ...patch, filename: this._uniqueFilename(patch.filename, uuid) }
      }
      Object.assign(synth, patch)
      this._persist()
    },

    _getTrack(uuid, trackIndex) {
      const synth = this.getSynth(uuid)
      return synth?.tracks?.[trackIndex] ?? null
    },

    _setTrackSequence(uuid, trackIndex, sequence) {
      const track = this._getTrack(uuid, trackIndex)
      if (!track) return false
      track.sequence = normalizeSequenceTimeline(sequence)
      this._persist()
      return true
    },

    updateSynthEvent(uuid, trackIndex, eventIndex, patch) {
      const track = this._getTrack(uuid, trackIndex)
      const event = track?.sequence?.[eventIndex]
      if (!event) return false
      if (patch.durationMs !== undefined && patch.durationTicks === undefined) {
        patch = { ...patch, durationTicks: patch.durationMs }
        delete patch.durationMs
      }
      if (patch.note !== undefined) {
        if (patch.note !== null && (!Number.isInteger(patch.note) || patch.note < 0 || patch.note > 127)) return false
      }
      if (patch.durationTicks !== undefined) {
        if (!Number.isFinite(Number(patch.durationTicks)) || Number(patch.durationTicks) <= 0) return false
      }
      if (patch.startTick !== undefined && (!Number.isFinite(Number(patch.startTick)) || Number(patch.startTick) < 0)) return false
      Object.assign(event, patch)
      if (event.note !== null && event.note !== undefined) event.note = Math.trunc(Number(event.note))
      event.durationTicks = Math.max(1, Math.trunc(Number(event.durationTicks) || SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS))
      event.startTick = Math.max(0, Math.trunc(Number(event.startTick) || 0))
      sortSequenceEvents(track.sequence)
      this._persist()
      return true
    },

    insertSynthEvent(uuid, trackIndex, afterEventIndex, event) {
      const track = this._getTrack(uuid, trackIndex)
      if (!track) return { ok: false, index: -1 }
      const normalized = normalizeSequenceEvent({
        note: event?.note === undefined ? SYNTH_DEFAULT_NOTE : event.note,
        startTick: event?.startTick ?? (track.sequence[afterEventIndex]
          ? track.sequence[afterEventIndex].startTick + track.sequence[afterEventIndex].durationTicks
          : 0),
        durationTicks: event?.durationTicks ?? event?.durationMs ?? SYNTH_DEFAULT_SEQUENCE_EVENT_DURATION_TICKS,
      })
      if (normalized.note == null) return { ok: false, index: -1 }
      track.sequence.push(normalized)
      sortSequenceEvents(track.sequence)
      const index = track.sequence.indexOf(normalized)
      this._persist()
      return { ok: true, index }
    },

    appendSynthEvent(uuid, trackIndex, event) {
      const track = this._getTrack(uuid, trackIndex)
      if (!track) return { ok: false, index: -1 }
      const endTick = track.sequence.reduce((max, item) => Math.max(max, item.startTick + item.durationTicks), 0)
      return this.insertSynthEvent(uuid, trackIndex, track.sequence.length - 1, { ...event, startTick: event?.startTick ?? endTick })
    },

    removeSynthEvent(uuid, trackIndex, eventIndex) {
      const track = this._getTrack(uuid, trackIndex)
      if (!track || eventIndex < 0 || eventIndex >= track.sequence.length) return false
      track.sequence.splice(eventIndex, 1)
      this._persist()
      return true
    },

    moveSynthEvent(uuid, trackIndex, eventIndex, direction) {
      const track = this._getTrack(uuid, trackIndex)
      if (!track) return false
      const target = direction === 'left' ? eventIndex - 1 : eventIndex + 1
      if (eventIndex < 0 || eventIndex >= track.sequence.length || target < 0 || target >= track.sequence.length) return false
      const item = track.sequence[eventIndex]
      const targetEvent = track.sequence[target]
      item.startTick = direction === 'left'
        ? Math.max(0, targetEvent.startTick)
        : targetEvent.startTick + targetEvent.durationTicks
      sortSequenceEvents(track.sequence)
      this._persist()
      return true
    },
  },
})

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}
